import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { adminApi, apiError, ORDER_STATUS, fmtDate } from "@/lib/api";
import { eur, eur2 } from "@/lib/product";
import { OrderDetail } from "./OrderDetail";
import { StripeBadge } from "./StripeBadge";

export const StatusBadge = ({ status }) => {
  const [label, cls] = ORDER_STATUS[status] || [status, "bg-slate-100"];
  return <span data-testid={`status-badge-${status}`} className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${cls}`}>{label}</span>;
};

export const OrdersTab = () => {
  const [filter, setFilter] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/orders", { params: filter ? { status: filter } : {} });
      setOrders(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <select data-testid="orders-status-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="field-input max-w-xs">
          <option value="">Tous les statuts</option>
          {Object.entries(ORDER_STATUS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <button type="button" onClick={load} data-testid="orders-refresh-button" className="btn-ghost-ice !py-2.5">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </button>
        <span className="text-sm text-slate-500">{orders.length} commande(s)</span>
      </div>
      <div className="admin-table-wrap">
        <table data-testid="orders-table" className="admin-table">
          <thead><tr><th>N°</th><th>Date</th><th>Client</th><th>Ville</th><th>Qté</th><th>Total</th><th>Relances</th><th>Stripe</th><th>Statut</th></tr></thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.order_id} data-testid={`order-row-${o.order_id}`} onClick={() => setSelected(o.order_id)} className="cursor-pointer">
                <td className="font-mono font-semibold">{o.order_id}</td>
                <td>{fmtDate(o.created_at)}</td>
                <td>{o.first_name} {o.last_name}<div className="text-xs text-slate-500">{o.email}</div></td>
                <td>{o.address.postal_code} {o.address.city}</td>
                <td>{o.quantity}</td>
                <td className="font-semibold">{eur(o.total)}{o.promo_code && <div className="text-xs font-normal text-emerald-700">{o.promo_code}</div>}</td>
                <td data-testid={`order-reminders-${o.order_id}`}>{o.reminder_count || 0}{o.last_reminder_at && <div className="text-xs text-slate-500">{fmtDate(o.last_reminder_at)}</div>}</td>
                <td><StripeBadge order={o} testId={`order-stripe-badge-${o.order_id}`} /></td>
                <td>
                  <StatusBadge status={o.status} />
                  {o.refunded_total > 0 && <div data-testid={`order-refund-badge-${o.order_id}`} className="mt-1 inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">−{eur2(o.refunded_total)}</div>}
                </td>
              </tr>
            ))}
            {!loading && orders.length === 0 && <tr><td colSpan={9} className="text-center text-slate-500 py-10">Aucune commande.</td></tr>}
          </tbody>
        </table>
      </div>
      <OrderDetail orderId={selected} onClose={() => setSelected(null)} onUpdated={load} />
    </div>
  );
};
